import { UserFeatureService } from '@hive/core/modules/cloud/services/user-feature';
import type { SettingTab } from '@hive/core/modules/dialogs/constant';
import { FeatureFlagService } from '@hive/core/modules/feature-flag';
import { SubscriptionStatus } from '@hive/graphql';
import { useI18n } from '@hive/i18n';
import {
  AppearanceIcon,
  ExperimentIcon,
  FolderIcon,
  InformationIcon,
  KeyboardIcon,
  MeetingIcon,
  NotificationIcon,
  PenIcon,
} from '@blocksuite/icons/rc';
import {
  useLiveData,
  useServiceOptional,
  useServices,
} from '@toeverything/infra';
import { useEffect } from 'react';

import {
  AuthService,
  ServerService,
  SubscriptionService,
  UserQuotaService,
  WorkspaceSubscriptionService,
} from '../../../../modules/cloud';
import type { SettingSidebarItem, SettingState } from '../types';
import { AboutHive } from './about';
import { AppearanceSettings } from './appearance';
import { BackupSettingPanel } from './backup';
import { BillingSettings } from './billing';
import { EditorSettings } from './editor';
import { ExperimentalFeatures } from './experimental-features';
import { PaymentIcon, UpgradeIcon } from './icons';
import { MeetingsSettings } from './meetings';
import { NotificationSettings } from './notifications';
import { HivePricingPlans } from './plans';
import { Shortcuts } from './shortcuts';

export type GeneralSettingList = SettingSidebarItem[];

export const useGeneralSettingList = (): GeneralSettingList => {
  const t = useI18n();
  const {
    authService,
    serverService,
    userFeatureService,
    featureFlagService,
    subscriptionService,
    userQuotaService,
  } = useServices({
    AuthService,
    ServerService,
    UserFeatureService,
    FeatureFlagService,
    SubscriptionService,
    UserQuotaService,
  });
  const status = useLiveData(authService.session.status$);
  const loggedIn = status === 'authenticated';
  const hasPaymentFeature = useLiveData(
    serverService.server.features$.map(f => f?.payment)
  );
  const enableEditorSettings = useLiveData(
    featureFlagService.flags.enable_editor_settings.$
  );
  const enableMeetings = useLiveData(
    featureFlagService.flags.enable_meetings.$
  );

  // Check if user has an active or trialing subscription (user-level)
  const userTeamSubscription = useLiveData(
    subscriptionService.subscription.team$
  );
  const hasActiveUserSubscription =
    userTeamSubscription?.status === SubscriptionStatus.Active ||
    userTeamSubscription?.status === SubscriptionStatus.Trialing;

  // Check if workspace has an active or trialing subscription (workspace-level)
  const workspaceSubscriptionService = useServiceOptional(
    WorkspaceSubscriptionService
  );
  const workspaceSubscription = useLiveData(
    workspaceSubscriptionService?.subscription.subscription$
  );
  const hasActiveWorkspaceSubscription =
    workspaceSubscription?.status === SubscriptionStatus.Active ||
    workspaceSubscription?.status === SubscriptionStatus.Trialing;

  // Check if user has a lifetime plan (from quota)
  const hasLifetimePlan = useLiveData(userQuotaService.quota.isLifetimePlan$);

  // Hide pricing plans if user has any active subscription (user or workspace level) or lifetime plan
  const hasActiveSubscription =
    hasActiveUserSubscription ||
    hasActiveWorkspaceSubscription ||
    hasLifetimePlan;

  useEffect(() => {
    userFeatureService.userFeature.revalidate();
    subscriptionService.subscription.revalidate();
    workspaceSubscriptionService?.subscription.revalidate();
    userQuotaService.quota.revalidate();
  }, [
    userFeatureService,
    subscriptionService,
    workspaceSubscriptionService,
    userQuotaService,
  ]);

  const settings: GeneralSettingList = [
    {
      key: 'appearance',
      title: t['com.hive.settings.appearance'](),
      icon: <AppearanceIcon />,
      testId: 'appearance-panel-trigger',
    },
    {
      key: 'shortcuts',
      title: t['com.hive.keyboardShortcuts.title'](),
      icon: <KeyboardIcon />,
      testId: 'shortcuts-panel-trigger',
    },
  ];
  if (loggedIn) {
    settings.push({
      key: 'notifications',
      title: t['com.hive.setting.notifications'](),
      icon: <NotificationIcon />,
      testId: 'notifications-panel-trigger',
    });
  }
  if (enableEditorSettings) {
    // add editor settings to second position
    settings.splice(1, 0, {
      key: 'editor',
      title: t['com.hive.settings.editorSettings'](),
      icon: <PenIcon />,
      testId: 'editor-panel-trigger',
    });
  }

  if (enableMeetings) {
    settings.push({
      key: 'meetings',
      title: t['com.hive.settings.meetings'](),
      icon: <MeetingIcon />,
      testId: 'meetings-panel-trigger',
    });
  }

  if (hasPaymentFeature) {
    // Only show "Pricing Plans" if user doesn't have an active subscription or trial
    if (!hasActiveSubscription) {
      settings.splice(4, 0, {
        key: 'plans',
        title: t['com.hive.payment.title'](),
        icon: <UpgradeIcon />,
        testId: 'plans-panel-trigger',
      });
    }
    if (loggedIn) {
      settings.splice(4, 0, {
        key: 'billing',
        title: t['com.hive.payment.billing-setting.title'](),
        icon: <PaymentIcon />,
        testId: 'billing-panel-trigger',
      });
    }
  }

  if (BUILD_CONFIG.isElectron) {
    settings.push({
      key: 'backup',
      title: t['com.hive.settings.workspace.backup'](),
      icon: <FolderIcon />,
      testId: 'backup-panel-trigger',
    });
  }

  return settings;
};

interface GeneralSettingProps {
  activeTab: SettingTab;
  onChangeSettingState: (settingState: SettingState) => void;
}

export const GeneralSetting = ({
  activeTab,
  onChangeSettingState,
}: GeneralSettingProps) => {
  switch (activeTab) {
    case 'shortcuts':
      return <Shortcuts />;
    case 'notifications':
      return <NotificationSettings />;
    case 'editor':
      return <EditorSettings />;
    case 'appearance':
      return <AppearanceSettings />;
    case 'meetings':
      return <MeetingsSettings />;
    case 'about':
      return <AboutHive />;
    case 'plans':
      return <HivePricingPlans />;
    case 'billing':
      return <BillingSettings onChangeSettingState={onChangeSettingState} />;
    case 'experimental-features':
      return <ExperimentalFeatures />;
    case 'backup':
      return <BackupSettingPanel />;
    default:
      return null;
  }
};
